// src/pages/Auth.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, sendPasswordResetEmail } from 'firebase/auth';
import { app } from '../utils/firebase';
import './Auth.css';

// Helper function to convert Firebase error codes to user-friendly messages
const getReadableErrorMessage = (error: any): string => {
  const errorCode = error.code;

  const errorMessages: {[key: string]: string} = {
    // Email/Password sign-in errors
    'auth/invalid-email': 'Please enter a valid email address',
    'auth/user-disabled': 'This account has been disabled. Please contact support',
    'auth/user-not-found': 'No account found with this email. Please sign up first',
    'auth/invalid-credential': 'Incorrect password. Please try again or reset your password',
    'auth/wrong-password': 'Incorrect password. Please try again or reset your password',
    'auth/email-already-in-use': 'An account with this email already exists. Please sign in instead',
    'auth/password-does-not-meet-requirements': 'Please use a stronger password of at least 10 characters with uppercase, lowercase, numbers and special characters',
    'auth/requires-recent-login': 'Please sign in again to continue. This action requires recent authentication',
    'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support',
    'auth/account-exists-with-different-credential': 'An account already exists with this email but with a different sign-in method',
    'auth/invalid-verification-code': 'The verification code is invalid. Please try again',
    'auth/invalid-verification-id': 'The verification ID is invalid. Please try again',
    'auth/code-expired': 'The verification code has expired. Please request a new one',
    'auth/provider-already-linked': 'This account is already linked with another provider',
    'auth/credential-already-in-use': 'This credential is already associated with a different user account',
    
    // Google sign-in errors
    'auth/popup-closed-by-user': 'Sign-in was cancelled. Please try again',
    'auth/cancelled-popup-request': 'Sign-in process was interrupted. Please try again',
    'auth/popup-blocked': 'Sign-in popup was blocked by your browser. Please allow popups for this site',
    'auth/unauthorized-domain': 'This domain is not authorized for OAuth operations. Please contact support',
    
    // Network errors
    'auth/network-request-failed': 'A network error occurred. Please check your connection and try again',
    'auth/timeout': 'The operation has timed out. Please try again',
    
    // Too many attempts and security
    'auth/too-many-requests': 'Too many sign-in attempts. Please try again later',
    'auth/app-deleted': 'The authentication service has been disabled. Please contact support',
    'auth/argument-error': 'An invalid argument was provided to an authentication method. Please try again',
    'auth/invalid-user-token': 'Your sign-in session has expired. Please sign in again',
    'auth/invalid-tenant-id': 'Invalid tenant configuration. Please contact support',
    'auth/missing-android-pkg-name': 'Missing Android package name. Please contact support',
    'auth/missing-app-credential': 'Missing app credential. Please contact support',
    'auth/missing-verification-code': 'Missing verification code. Please try again',
    'auth/missing-verification-id': 'Missing verification ID. Please try again',
    'auth/missing-iframe-start': 'Authentication process interrupted. Please try again',
    'auth/missing-or-invalid-nonce': 'Authentication request contains an invalid nonce. Please try again',
    'auth/missing-phone-number': 'Please enter a valid phone number',
    'auth/missing-multi-factor-info': 'Missing two-factor authentication information. Please try again',
    'auth/missing-multi-factor-session': 'Two-factor authentication session expired. Please try again',
    'auth/phone-number-already-exists': 'This phone number is already in use by another account',
    'auth/quota-exceeded': 'Authentication quota exceeded. Please try again later',
    'auth/rejected-credential': 'The authentication attempt was rejected. Please try again',
    'auth/second-factor-already-in-use': 'This second factor is already associated with an account',
    'auth/maximum-second-factor-count-exceeded': 'Maximum number of second factors reached for this account',
    'auth/session-expired': 'Your authentication session has expired. Please sign in again',
    'auth/web-storage-unsupported': 'Your web browser does not support web storage. Please enable cookies or try a different browser',
  };
  
  return errorMessages[errorCode] || 'An error occurred during authentication. Please try again';
};

const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

const Auth: React.FC = () => {
  const [isRegister, setIsRegister] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(getReadableErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setLoading(true);
    try {
      if (isRegister) {
        await createUserWithEmailAndPassword(auth, email, password);
        navigate('/', { replace: true });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        navigate('/', { replace: true });
      }
    } catch (err: any) {
      setError(getReadableErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccessMessage("Password reset email sent! Please check your inbox.");
      // Don't reset the form completely so user can see their email
    } catch (err: any) {
      setError(getReadableErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  
  const toggleResetPassword = () => {
    setIsResetPassword(!isResetPassword);
    setError(null);
    setSuccessMessage(null);
  };

  return (
    <div className="auth-page-wrapper">
      <div className="auth-container">
      <h2>
        {isResetPassword
          ? 'Reset your password'
          : isRegister
            ? 'Sign up to NatureDr'
            : 'Sign in to NatureDr'}
      </h2>
      
      {!isResetPassword && (
        <>
          <button className="google-btn" onClick={handleGoogleSignIn} disabled={loading}>
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google logo" className="google-logo" />
            Continue with Google
          </button>
          <div className="or-divider"><span>or</span></div>
        </>
      )}
      
      {isResetPassword ? (
        <form className="auth-form" onSubmit={handlePasswordReset}>
          <p className="reset-instructions">Enter your email address and we'll send you a link to reset your password.</p>
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          <button type="submit" disabled={loading}>
            Send reset link
          </button>
          <button type="button" onClick={toggleResetPassword} disabled={loading} className="secondary-btn">
            Back to sign in
          </button>
        </form>
      ) : (
        <form className="auth-form" onSubmit={handleEmailAuth}>
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            autoComplete={isRegister ? "new-password" : "current-password"}
          />
          <button type="submit" disabled={loading}>
            {isRegister ? 'Sign up' : 'Sign In'}
          </button>
          
          {!isRegister && (
            <div className="forgot-password">
              <button 
                type="button" 
                onClick={toggleResetPassword} 
                disabled={loading} 
                className="link-btn"
              >
                Forgot password?
              </button>
            </div>
          )}
        </form>
      )}
      
      {!isResetPassword && (
        <div className="auth-toggle">
          {isRegister ? (
            <>
              Already have an account?{' '}
              <button type="button" onClick={() => setIsRegister(false)} disabled={loading} className="link-btn">Sign in</button>
            </>
          ) : (
            <>
              New to NatureDr?{' '}
              <button type="button" onClick={() => setIsRegister(true)} disabled={loading} className="link-btn">Create account</button>
            </>
          )}
        </div>
      )}
      
      {error && <div className="auth-error">{error}</div>}
      {successMessage && <div className="auth-success">{successMessage}</div>}
      </div>
    </div>
  );
};

export default Auth;
